import { formatNumber, EMPTY_DISPLAY } from '@/utils/core/format';
import { POSITIONING_LINE, POSITIONING_INDICATOR, POSITIONING_DECIMALS } from '@/utils/domain/competitorPositioning';

export const NOT_TOTALLED = '·';

export const POSITIONING_COLUMNS = Object.freeze([
	{ key: 'pdl', labelKey: 'PositioningPdl' },
	{ key: 'linear', labelKey: 'PositioningLinear' },
	{ key: 'dn', labelKey: 'PositioningDn' },
	{ key: 'presentTheoretical', labelKey: 'PositioningPresentTheoretical' },
	{ key: 'pdo', labelKey: 'PositioningPdo' },
	{ key: 'facings', labelKey: 'PositioningFacings' },
]);

const INDICATOR_LABEL_KEYS = Object.freeze({
	[POSITIONING_INDICATOR.PDL]: 'PositioningPdl',
	[POSITIONING_INDICATOR.PDO]: 'PositioningPdo',
	[POSITIONING_INDICATOR.FACINGS]: 'PositioningFacings',
});

function decimal(value) {
	return formatNumber(value, { decimals: POSITIONING_DECIMALS });
}

export function brandNames(t, tBrand = t) {
	return { myBrand: tBrand('PositioningMyBrand'), subset: tBrand('PositioningSubset'), myBrandTag: t('MyBrand'), subsetTag: t('Subset') };
}

function wording(value) {
	return String(value ?? '').toLowerCase();
}

function sameWording(a, b) {
	return wording(a).trim() === wording(b).trim();
}

function presentLine(line, names, t) {
	const isMyBrand = line.kind === POSITIONING_LINE.MY_BRAND;
	const isSubset = line.kind === POSITIONING_LINE.SUBSET;
	const name = isMyBrand ? names.myBrand : isSubset ? names.subset : line.label || t('competitor');

	let tags = [];
	if (!isSubset && line.absent) tags = [{ text: t('BrandAbsent'), color: 'error' }];
	else if (isMyBrand && !sameWording(name, names.myBrandTag)) tags = [{ text: names.myBrandTag, color: 'primary' }];
	else if (isSubset && !sameWording(name, names.subsetTag)) tags = [{ text: names.subsetTag, color: 'primary' }];

	let presentTheoretical = EMPTY_DISPLAY;
	if (isMyBrand) presentTheoretical = `${formatNumber(line.present)} / ${formatNumber(line.theoretical)}`;
	else if (!isSubset) presentTheoretical = formatNumber(line.present);

	return {
		...line,
		name,
		tags,
		caption: isSubset ? `${names.subsetTag} ${names.myBrand}` : '',
		showBalance: isMyBrand,
		display: {
			pdl: decimal(line.pdl),
			linear: decimal(line.linear),
			dn: decimal(line.dn),
			presentTheoretical,
			pdo: decimal(line.pdo),
			facings: formatNumber(line.facings),
		},
	};
}

function indicatorNames(indicators, t) {
	return indicators.map((indicator) => t(INDICATOR_LABEL_KEYS[indicator]).replace(/\s*%\s*$/, '')).join(', ');
}

function summaryItem(category, t) {
	const myBrandLine = category.lines.find((line) => line.kind === POSITIONING_LINE.MY_BRAND);
	const rankPrefix = t('RankNumber');
	const on = t('RankOn');

	if (myBrandLine.absent) {
		return { key: category.id, label: category.label, strong: t('BrandAbsent').toLowerCase(), rest: `(${myBrandLine.display.presentTheoretical} ${t('Referenced')})` };
	}

	const tops = category.myBrandTopIndicators;
	if (category.myBrandRank === 1) {
		return { key: category.id, label: category.label, strong: `${rankPrefix}1`, rest: `${on} ${indicatorNames(tops, t)}` };
	}
	if (category.myBrandRank != null) {
		const leads = tops.length ? ` · ${rankPrefix}1 ${on} ${indicatorNames(tops, t)}` : '';
		return { key: category.id, label: category.label, strong: `${rankPrefix}${category.myBrandRank}`, rest: `${on} ${indicatorNames([POSITIONING_INDICATOR.PDL], t)}${leads}` };
	}
	if (tops.length) {
		return { key: category.id, label: category.label, strong: `${rankPrefix}1`, rest: `${on} ${indicatorNames(tops, t)}` };
	}
	return null;
}

export function presentPositioning(result, t, tBrand = t) {
	const names = brandNames(t, tBrand);
	const categories = (result?.categories || []).map((category) => {
		const lines = category.lines.map((line) => presentLine(line, names, t));
		return {
			...category,
			lines,
			totalDisplay: {
				pdl: NOT_TOTALLED,
				linear: decimal(category.total.linear),
				dn: NOT_TOTALLED,
				presentTheoretical: formatNumber(category.total.references),
				pdo: NOT_TOTALLED,
				facings: formatNumber(category.total.facings),
			},
		};
	});

	return {
		categories,
		summary: {
			tag: names.myBrandTag,
			name: sameWording(names.myBrand, names.myBrandTag) ? '' : names.myBrand,
			items: categories.map((category) => summaryItem(category, t)).filter(Boolean),
		},
	};
}
